/**
 * Turning a validated answer into reference cards.
 *
 * ── EVERY FIELD HERE WAS WRITTEN BY THE SERVER, FROM THE RECORD ─────────────
 * `D19` is the product's central safety claim: measured citation hallucination
 * runs 14%–95% depending on model, and structure fixes it where prompting does
 * not. The model emits an IDENTIFIER and nothing else. Author, year, journal,
 * DOI, evidence level and studied population are rendered by
 * `@repo/agents` (`src/core/trainer/citation-renderer.ts`) from the record's own
 * frontmatter, and an
 * answer that writes any of them itself is REJECTED and regenerated — there is
 * a check for a bare DOI, a bare "et al." and a bare parenthesised year.
 *
 * So this file parses SERVER PROSE, not model prose. That distinction is the
 * whole reason it is allowed to exist, and it is why the parser is pinned to
 * the exact renderer that produces the text: `__tests__/citations.test.ts`
 * reads that same file — through the `@repo/agents/citation-source` subpath —
 * and fails if any renderer line it depends on changes. A drifted format does not throw here — it silently
 * matches nothing, and a screen with no reference cards looks like an answer
 * that had no sources, which is the one appearance this product cannot afford.
 *
 * ── WHY NOT A METADATA CHANNEL ON THE WIRE ──────────────────────────────────
 * The obvious alternative is to send the `RenderedCitation[]` the server
 * already has as message metadata. It was not chosen, for a reason that comes
 * straight out of `T-57`: metadata arrives as its own frame, and a Durable
 * Object that dies mid-answer sends no more frames — it replays what it
 * buffered and marks it `done`. Cards parsed from the INLINE expansions are
 * exactly as durable as the sentences they belong to. Truncate the answer
 * anywhere and every claim still standing still carries its source, which is
 * the property `inline` was written to have.
 *
 * ── DORMANT TODAY (`D47`'s 2026-08-27 amendment) ──────────────────────────
 * `CITATION_RENDERING_ENABLED` in `@repo/agents`
 * (`src/core/trainer/citation-renderer.ts`) is `false`, so the server renders
 * no inline expansion and no sources block, and every parser here therefore
 * finds nothing in a real answer: `readCitations` returns `[]`, no reference
 * card is drawn, and `collapseCitations` passes the prose through. The source
 * is fetched on demand instead — the model re-queries the knowledge base
 * through the `source-lookup` skill when the reader asks where something came
 * from.
 *
 * The file is KEPT rather than deleted, which `D47`'s amendment requires — the
 * same rule `D21`'s amendment applies to the refusal gate: one flag in the
 * repository rather than two, and re-enabling is a constant rather than a
 * rewrite. `__tests__/citations.test.ts` therefore still exercises the full
 * parser against the server's own output shape — both states are tested, or the
 * day the flag flips back is a leap in the dark — and it also asserts, through
 * the same `?raw` drift guard, that the server flag really is off. The two
 * halves cannot be flipped independently: this parser is fed by that renderer
 * and by nothing else.
 */
/**
 * The inline expansion, as the renderer's `inline` writes it:
 *
 *     [1: Pelland JC, Remmert JF, Robinson ZP et al., 2024, SportRxiv, doi:10.51224/SRXIV.501]
 *
 * ── WHY THE PARTS ARE ANCHORED FROM BOTH ENDS ───────────────────────────────
 * The author list contains commas, and so may a journal name, so splitting on
 * `,` is ambiguous in both directions. The two fixed points are the four-digit
 * year and the literal `, doi:` before the closing bracket; the lazy groups are
 * pinned between them.
 *
 * `.` deliberately excludes newlines. An expansion is written on one line, so a
 * pattern that could span them would, on a malformed answer, swallow a
 * paragraph into a "journal name" rather than simply not matching.
 */
const INLINE_CITATION = /\[(\d+): (.+?), (\d{4}), (.+?), doi:([^\]\s]+)\]/g;
/**
 * The sources block's header, exactly as the renderer's `sources` opens it. Used both to
 * find the block and to cut it off the displayed prose — the screen renders the
 * references as cards instead, and showing both would be the same nine facts
 * twice on a phone screen.
 */
const SOURCES_HEADER = "\n---\n\n**Sources**\n";
/**
 * One entry inside that block:
 *
 *     1. Pelland JC, Remmert JF (2024). *SportRxiv*. preprint. doi:10.51224/…
 *        Studied in: 79.1% male, mean age 25.2
 *        > an optional validated quote
 *
 * Anchored at a line start, so a `1.` inside the prose above cannot open one.
 */
const SOURCE_ENTRY = /^(\d+)\. (.+?) \((\d{4})\)\. \*(.+?)\*\. (.+?)\. doi:(\S+)$/;
const STUDIED_IN = /^\s+Studied in: (.+)$/;
const SOURCE_QUOTE = /^\s+> (.+)$/;
/**
 * A DOI is a URL path segment, and it is NOT re-encoded here.
 *
 * `encodeURIComponent` would escape the `/` that separates the registrant from
 * the suffix, and doi.org answers 404 for the escaped form — a citation that
 * fails to open, on the one tap the whole product exists to make possible. The
 * shape is checked instead: `10.` then the registrant, a slash, and a suffix.
 * Anything else gets no link rather than a broken one.
 */
const DOI_SHAPE = /^10\.\d{4,9}\/\S+$/;
export function doiUrl(doi) {
    return DOI_SHAPE.test(doi) ? `https://doi.org/${doi}` : null;
}
/**
 * Read the sources block, keyed by ordinal. Absent block → an empty map, which
 * is the truncated case and is deliberately not an error here: the caller still
 * has the inline expansions, and `assessAnswer` is what tells the user the rest
 * is missing.
 */
function readSourceDetails(text) {
    const details = new Map();
    const start = text.indexOf(SOURCES_HEADER);
    if (start < 0) {
        return details;
    }
    let current = null;
    for (const line of text.slice(start).split("\n")) {
        const entry = SOURCE_ENTRY.exec(line);
        if (entry !== null) {
            current = {
                authors: entry[2],
                evidenceLevel: entry[5],
                population: null,
                quote: null,
            };
            details.set(Number(entry[1]), current);
            continue;
        }
        if (current === null) {
            continue;
        }
        const studied = STUDIED_IN.exec(line);
        if (studied !== null) {
            current.population = studied[1];
            continue;
        }
        const quote = SOURCE_QUOTE.exec(line);
        if (quote !== null) {
            current.quote = quote[1];
        }
    }
    return details;
}
/**
 * Every citation in a rendered answer, in the order the prose cites them.
 *
 * Deduplicated by ordinal: the server numbers each record once and expands the
 * SAME reference at every mention, so an answer that cites record 1 three times
 * carries three identical expansions and must produce one card.
 */
export function readCitations(text) {
    const details = readSourceDetails(text);
    const seen = new Set();
    const citations = [];
    for (const match of text.matchAll(INLINE_CITATION)) {
        const ordinal = Number(match[1]);
        if (seen.has(ordinal)) {
            continue;
        }
        seen.add(ordinal);
        const detail = details.get(ordinal);
        const doi = match[5];
        citations.push({
            // The block's list is the COMPLETE one; the inline form abbreviates past
            // three names with `et al.`. Prefer the complete one when it arrived.
            authors: detail?.authors ?? match[2],
            doi,
            doiUrl: doiUrl(doi),
            evidenceLevel: detail?.evidenceLevel ?? null,
            journal: match[4],
            ordinal,
            population: detail?.population ?? null,
            quote: detail?.quote ?? null,
            year: Number(match[3]),
        });
    }
    return citations.sort((left, right) => left.ordinal - right.ordinal);
}
/**
 * The prose, with each inline expansion collapsed to its bare number and the
 * sources block removed.
 *
 * ── COLLAPSED, NOT DELETED ──────────────────────────────────────────────────
 * `[1]` stays exactly where `inline` put it, at the point of the claim.
 * Deleting the marker would leave a prescriptive number sitting in a sentence
 * with nothing tying it to a card, which is precisely the uncited prescription
 * `D19` exists to prevent — the reference would still be ON the screen, but
 * nothing would say which sentence it belongs to.
 *
 * The sources block is cut wherever it begins, complete or not. A block that
 * stops mid-entry would otherwise put the same facts on screen twice, once
 * mangled — and it would still not be the thing that tells the reader the
 * answer was cut off. `assessAnswer` is, and it reads the ORIGINAL text rather
 * than this one, which is why nothing is lost by cutting here.
 */
export function collapseCitations(text) {
    const withoutBlock = (() => {
        const start = text.indexOf(SOURCES_HEADER);
        return start < 0 ? text : text.slice(0, start);
    })();
    const collapsed = withoutBlock.replace(INLINE_CITATION, (_whole, ordinal) => `[${ordinal}]`);
    return tidyMarkerResidue(collapsed).trimEnd();
}
/**
 * The space a `D47`-amendment erasure leaves behind.
 *
 * With `CITATION_RENDERING_ENABLED` off the server's `inline` returns `""`, and
 * the engine substitutes it at the marker's position with a generic `replace`
 * that knows nothing about the sentence around it. `"… per week
 * [[weekly-volume-id]]."` therefore arrives as `"… per week ."`, and two
 * markers in a row leave a double space.
 *
 * ── WHY IT IS TIDIED HERE AND NOT IN THE ENGINE ─────────────────────────────
 * `core/service/citation.ts` is the portable engine (`D45`): it expands a
 * marker and appends a block, and teaching it which punctuation may not be
 * preceded by a space is teaching it English. This file already exists to
 * prepare server prose for display, so the presentational half of the erasure
 * belongs with the presentational half of the rendering.
 *
 * ── BOUNDED ON PURPOSE ──────────────────────────────────────────────────────
 * Only spaces and tabs, only after a non-space character, and only before
 * sentence punctuation — never `\s`, which would swallow the newlines markdown
 * reads as paragraph and list structure, and never at a line start, which is
 * where a nested list item's indentation lives. With the flag ON this is a
 * no-op on every fixture in `__tests__/citations.test.ts`: `inline` renders its
 * closing bracket immediately before the full stop, with no space to remove.
 */
function tidyMarkerResidue(text) {
    return text
        .replace(/(\S)[ \t]+([.,;:!?])/g, "$1$2")
        .replace(/(\S)[ \t]{2,}/g, "$1 ");
}
//# sourceMappingURL=citations.js.map